import { ProductTypeEnum } from "API";
import clsx from "clsx";
import { AddToCartIcon } from "components/Icons";
import Image from "components/Image/Image";
import TripDatesModal from "containers/TripDatesModal/TripDatesModal";
import { CartContext } from "contexts/cart/cart.context";
import { ModalContext } from "contexts/modal.provider";
import { StockItemContext } from "contexts/stock/stock.provider";
import { cartAnimation } from "helper/utility";
import React, { useContext, useEffect, useState } from "react";
import BlockUi from "react-block-ui";
import notifications from "utils/notifications";

import { AddToCartTextButton } from "../Button/Button";
import InputIncDec from "../InputIncDec/InputIncDec";
import { ProductCardWrapper, ProductInfo } from "./ProductCard.style";

type ProductCardProps = {
    product: any;
    onClick?: (e: any) => void;
};

const ProductCard: React.FC<ProductCardProps> = ({ product, onClick }) => {
    const [loading, setLoading] = useState(null);
    const [pendingAdd, setPendingAdd] = useState(null);
    const [stockItems, setStockItems] = useState(null);
    const {
        add,
        getProductTripTotal,
        getProductPrice,
        update,
        cart: {
            products,
            tripDays,
            orderStartTime,
            orderEndTime,
            tripDiscountMultiplier,
            stockLocation,
        },
    } = React.useContext(CartContext);

    const cartProduct = product && products.find((p) => p.id === product.id);
    const { openModal, closeModal } = useContext(ModalContext);
    const { getAvailableQty } = useContext(StockItemContext);

    const imageUrl = product.image?.url ?? product.media?.items[0]?.media?.url;

    useEffect(() => {
        if (orderStartTime && orderEndTime) {
            getAvailableQty(
                product.id,
                orderStartTime,
                orderEndTime,
                stockLocation
            ).then((rsp) => {
                setStockItems(rsp);
            });
        }
    }, []);

    useEffect(() => {
        if (orderStartTime && orderEndTime) {
            setLoading(true);
            getAvailableQty(product.id, orderStartTime, orderEndTime, stockLocation)
                .then((rsp) => {
                    setStockItems(rsp);
                    if (pendingAdd) {
                        if (rsp > 0) {
                            document.getElementById(`add-${product.id}`)?.click();
                        } else {
                            notifications.info(
                                `There are no available stock for your selected dates`,
                                8000
                            );
                        }
                        setPendingAdd(null);
                    }
                })
                .finally(() => setLoading(false));
        }
    }, [orderStartTime, orderEndTime, pendingAdd, stockLocation]);

    const handleClick = (e: any) => {
        e.stopPropagation();
        if (!orderStartTime) {
            setPendingAdd(true);
            openModal({
                content: <TripDatesModal onOk={closeModal} />,
                classes: "overflow-visible",
            });
        } else {
            if (imageUrl) {
                cartAnimation(e);
            }
            add(product);
        }
    };

    const handleUpdate = (value: number) => {
        if (!cartProduct && value === 1) {
            add(product);
        } else {
            update(product.id, value);
        }
    };

    return (
        <BlockUi tag="div" blocking={loading}>
            <ProductCardWrapper
                onClick={onClick}
                className={clsx(
                    stockItems === 0 ? " bg-black opacity-75" : "",
                    "product-card mt-0 transition-transform transform hover:scale-105 hover:shadow-lg"
                )}
            >
                <div>
                    <Image
                        url={
                            product.image?.url ??
                            (product.media && product.media.items[0]
                                ? product.media.items[0].media?.url
                                : null)
                        }
                        className="product-image rounded-t-lg object-contain w-full"
                        style={{ position: "relative", height: 185 }}
                        alt={product.name}
                    />
                </div>
                <div className="relative">
                    <div className="h-16">
                        <p className="md:truncate-overflow mx-4 text-gray-800 text-md font-semibold mt-2 line-clamp-3">
                            {product.name}
                        </p>
                    </div>
                    <ProductInfo className="mx-4 mt-2">
                        <span className="product-weight">{product.weight}</span>
                        <div className="product-meta ">
                            <div className="flex items-center justify-start flex-col sm:w-full">
                                {!tripDiscountMultiplier || tripDiscountMultiplier === 1 ? (
                                    <div className="w-full">
                                        <h4 className="text-left mt-1 text-gray-600 font-semibold text-lg">
                                            R{product.price}
                                            {product.type !== ProductTypeEnum.RETAIL_ITEM ? (
                                                <span className="inline-flex ml-1 text-sm text-gray-600">
                                                    /day
                                                </span>
                                            ) : (
                                                <span className="ml-1 inline-flex justify-left items-center px-2 py-0.5 rounded text-sm font-semibold leading-4 bg-blue-100 text-blue-800">
                                                    buy
                                                </span>
                                            )}
                                        </h4>
                                    </div>
                                ) : (
                                    <div className="w-full">
                                        {product.type !== ProductTypeEnum.RETAIL_ITEM && (
                                            <span className="line-through text-red-500 font-bold">
                                                <h4 className="flex mt-1 text-gray-400 font-semibold text-lg">
                                                    R{product.price}
                                                    {product.type !== ProductTypeEnum.RETAIL_ITEM && (
                                                        <span className="inline-flex ml-1 text-sm text-gray-600 no-underline">
                                                            /day
                                                        </span>
                                                    )}
                                                </h4>
                                            </span>
                                        )}
                                        <h4 className="flex mt-2 text-gray-600 font-semibold text-lg">
                                            R
                                            {getProductPrice(
                                                tripDays,
                                                product.price,
                                                product.type
                                            ).toFixed(2)}
                                            {product.type !== ProductTypeEnum.RETAIL_ITEM ? (
                                                <span className="inline-flex ml-1 text-sm text-gray-600">
                                                    /day
                                                </span>
                                            ) : (
                                                <span className="ml-1 inline-flex justify-left items-center px-2 py-0.5 rounded text-sm font-semibold leading-4 bg-blue-100 text-blue-800">
                                                    buy
                                                </span>
                                            )}
                                        </h4>
                                    </div>
                                )}

                                {stockItems === 0 && (
                                    <span className="sm:hidden inline-flex items-center my-2 px-2 py-0.5 rounded text-sm font-semibold leading-4 bg-orange-100 text-orange-800">
                                        No stock for your dates
                                    </span>
                                )}

                                {stockItems !== 0 &&
                                    tripDays > 0 &&
                                    product.type !== ProductTypeEnum.RETAIL_ITEM && (
                                        <div className="my-1 w-full text-sm text-gray-700 font-semibold tracking-wide">
                                            Total: R{" "}
                                            {getProductTripTotal(
                                                product,
                                                cartProduct?.qty
                                            ).total.toFixed(2)}
                                        </div>
                                    )}

                                {cartProduct?.qty >= stockItems && (
                                    <div className="w-full text-red-500 text-xs tracking-wide">
                                        Contact us to book {stockItems + 1}+
                                    </div>
                                )}
                            </div>

                            {/*Mobile */}
                            {stockItems !== 0 && (
                                <div className="sm:hidden flex items-center justify-center mt-4 mb-2">
                                    {!cartProduct || cartProduct?.qty <= 0 ? (
                                        <AddToCartIcon
                                            className="text-scuttle-orange w-6 h-6"
                                            onClick={handleClick}
                                        />
                                    ) : (
                                        <InputIncDec
                                            disabled={cartProduct?.qty >= stockItems}
                                            type={"horizontal"}
                                            value={cartProduct?.qty}
                                            onClick={(e: any) => {
                                                e.stopPropagation(onclick);
                                            }}
                                            onUpdate={(value: number) => handleUpdate(value)}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="hidden sm:flex flex justify-center mt-4 mb-2">
                            {stockItems === 0 ? (
                                <span className="inline-flex items-center text-center px-2 py-0.5 rounded text-xs font-semibold leading-4 bg-yellow-500 text-white">
                                    Out of stock for your rental dates
                                </span>
                            ) : (
                                <>
                                    {!cartProduct || cartProduct?.qty <= 0 ? (
                                        <AddToCartTextButton
                                            id={`add-${product.id}`}
                                            onClick={handleClick}
                                        />
                                    ) : (
                                        <InputIncDec
                                            disabled={cartProduct?.qty >= stockItems}
                                            type={"horizontal"}
                                            value={cartProduct?.qty}
                                            onClick={(e: any) => {
                                                e.stopPropagation(onclick);
                                            }}
                                            onUpdate={(value: number) => handleUpdate(value)}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </ProductInfo>
                </div>
            </ProductCardWrapper>
        </BlockUi>
    );
};

export default ProductCard;
